import { HttpClient, HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import { inject, Injectable, isDevMode, signal } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { finalize, map } from 'rxjs';
import { ToastService } from '~ngx-shared/layout';
import { QueryUtil } from '~ngx-shared/utils';

export type FileStatus = {
  [key: string]: {
    isOpening?: boolean;
    isDownloading?: boolean;
  };
};

@Injectable({ providedIn: 'root' })
export class FileService {
  private readonly apollo = inject(Apollo);
  private readonly httpClient = inject(HttpClient);
  private readonly toastService = inject(ToastService);

  readonly status = signal<FileStatus>({});

  private _uploadUrl = '';
  private _downloadUrl = '';

  init(uploadUrl: string, downloadUrl: string) {
    this._uploadUrl = uploadUrl;
    this._downloadUrl = downloadUrl;
  }

  // META

  getFileMetas$<T>(ids: string[], tableName: string, identifier?: string) {
    return this.apollo
      .query<{ files: T[] }>({
        query: gql`
        query ReadFileMeta($where: ${tableName}_bool_exp) {
          files: ${tableName}(where: $where) {
            ${!!identifier ? 'id: ' + identifier : 'id'}
            name
            size
          }
        }
      `,
        variables: {
          where: { _or: { [!!identifier ? identifier : 'id']: { _in: ids || [] } } }
        }
      })
      .pipe(map(queryResult => QueryUtil.dataOrThrow(queryResult).files));
  }

  // UPLOAD

  uploadFile$(file: File, namespace: string) {
    let mimeType = file.type;
    if (file.name.endsWith('.msg')) {
      mimeType = 'application/vnd.ms-outlook';
    }

    const request = new HttpRequest<File>('POST', `${this._uploadUrl}/${namespace}`, file, {
      headers: new HttpHeaders({
        'Content-Type': 'application/octet-stream'
      }),
      params: new HttpParams({
        fromObject: {
          'mime-type': mimeType,
          name: file.name,
          // WebKit 319985: Safari 26.5+ loses the body when the service worker re-fetches it.
          'ngsw-bypass': 'true'
        }
      }),
      reportProgress: true,
      responseType: 'text'
    });
    return this.httpClient.request<string>(request);
  }

  //

  getFileUrl(id?: string, namespace?: string): string {
    return `${this._downloadUrl}/${namespace}/${id}`;
  }

  // DOWNLOAD

  downloadFile(
    id?: string,
    namespace?: string,
    fileNameToDownload?: string,
    queryParams?: HttpParams
  ): void {
    const status = this.status();
    if (id && namespace && !status[id]?.isDownloading) {
      status[id] = { isDownloading: true, isOpening: !fileNameToDownload };
      this.status.set(status);
      this.httpClient
        .get(this.getFileUrl(id, namespace), {
          params: queryParams,
          responseType: 'blob'
        })
        .pipe(
          finalize(() => {
            const status = this.status();
            status[id] = { isDownloading: false, isOpening: false };
            this.status.set(status);
          })
        )
        .subscribe(fileBlob => {
          this.openFileBlob(fileBlob, fileNameToDownload);
        });
    }
  }

  // OPEN

  openBlob(data: any, options?: BlobPropertyBag, fileNameToDownload?: string) {
    this.openFileBlob(new Blob([data], options), fileNameToDownload);
  }

  openFileBlob(blob: Blob, fileNameToDownload?: string) {
    if (blob) {
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.setAttribute('style', 'display: none');
      a.href = objectUrl;
      a.target = '_blank';

      if (!isDevMode() && fileNameToDownload) {
        a.download = fileNameToDownload;
      }
      a.click();
      document.body.removeChild(a);
      this.toastService.toastSuccess();
    } else {
      this.toastService.toastError();
    }
  }
}
